import { usePage } from "@inertiajs/react"
import { useEffect } from "react"

type WechatJsSdkConfig = {
  appId: string
  timestamp: number
  nonceStr: string
  signature: string
  jsApiList: string[]
}

type PageProps = {
  shareImageUrl?: string
  wechatJsSdk?: WechatJsSdkConfig | null
}

type WechatSdk = {
  config: (config: WechatJsSdkConfig & { debug: boolean }) => void
  ready: (callback: () => void) => void
  error: (callback: () => void) => void
  updateAppMessageShareData?: (data: {
    title: string
    desc: string
    link: string
    imgUrl: string
  }) => void
  updateTimelineShareData?: (data: {
    title: string
    link: string
    imgUrl: string
  }) => void
}

declare global {
  interface Window {
    wx?: WechatSdk
  }
}

const WECHAT_SDK_SRC = "https://res.wx.qq.com/open/js/jweixin-1.6.0.js"
let wechatSdkPromise: Promise<void> | null = null

function isWechatBrowser() {
  if (typeof navigator === "undefined") return false
  return /MicroMessenger/i.test(navigator.userAgent)
}

function loadWechatSdk() {
  if (typeof document === "undefined") return Promise.resolve()
  if (window.wx) return Promise.resolve()
  if (wechatSdkPromise) return wechatSdkPromise

  wechatSdkPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${WECHAT_SDK_SRC}"]`,
    )
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true })
      existing.addEventListener("error", () => reject(), { once: true })
      return
    }

    const script = document.createElement("script")
    script.src = WECHAT_SDK_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject()
    document.head.appendChild(script)
  })

  return wechatSdkPromise
}

function metaContent(selector: string) {
  return document.querySelector<HTMLMetaElement>(selector)?.content?.trim() || ""
}

function shareData(fallbackImageUrl?: string) {
  const title = metaContent('meta[property="og:title"]') || document.title
  const desc =
    metaContent('meta[property="og:description"]') ||
    metaContent('meta[name="description"]')
  const link =
    document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href ||
    metaContent('meta[property="og:url"]') ||
    window.location.href.split("#", 1)[0]
  const imgUrl =
    metaContent('meta[property="og:image"]') ||
    metaContent('meta[name="twitter:image"]') ||
    fallbackImageUrl ||
    ""

  return { title, desc, link, imgUrl }
}

export function WechatShareBridge() {
  const { url, props } = usePage<PageProps>()
  const config = props.wechatJsSdk
  const fallbackImageUrl = props.shareImageUrl

  useEffect(() => {
    if (!config || !isWechatBrowser()) return

    let canceled = false

    void loadWechatSdk()
      .then(() => {
        if (canceled || !window.wx) return

        window.wx.config({ ...config, debug: false })
        window.wx.ready(() => {
          if (canceled || !window.wx) return

          const data = shareData(fallbackImageUrl)
          if (!data.imgUrl) return

          window.wx.updateAppMessageShareData?.({
            title: data.title,
            desc: data.desc,
            link: data.link,
            imgUrl: data.imgUrl,
          })
          window.wx.updateTimelineShareData?.({
            title: data.title,
            link: data.link,
            imgUrl: data.imgUrl,
          })
        })
        window.wx.error(() => {})
      })
      .catch(() => {})

    return () => {
      canceled = true
    }
  }, [config, fallbackImageUrl, url])

  return null
}
